import React, { useCallback, useEffect, useState } from 'react';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { btn, card, input, fmtDateTime } from './agentUi';

/** Verified forwarding addresses (D3): mail from these counts as you. */
const EmailAliasManager: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const [aliases, setAliases] = useState<Array<{ id: string; email: string; verifiedAt: string | null; verificationSentAt: string | null }>>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    agentApi.aliases().then((r) => setAliases(r.aliases)).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      const r: any = await agentApi.addAlias(email.trim());
      setNotice(r?.autoVerified ? `${email.trim()} added and verified.` : r?.warning ? r.warning : `Verification email sent to ${email.trim()} — click the link in it to finish.`);
      setEmail('');
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add the address'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={compact ? '' : `${card} p-4`}>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Forwarding addresses</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Email forwarded to the agent mailbox from these verified addresses is handled as you.</p>
      <ul className="mt-3 space-y-1.5">
        {aliases.length === 0 && <li className="text-sm text-gray-500">None yet.</li>}
        {aliases.map((a) => (
          <li key={a.id} className="flex items-center gap-2 text-sm">
            <span className="text-gray-900 dark:text-gray-100">{a.email}</span>
            {a.verifiedAt ? (
              <span className="text-xs text-green-700 dark:text-green-400">verified</span>
            ) : (
              <>
                <span className="text-xs text-amber-700 dark:text-amber-300">pending · sent {fmtDateTime(a.verificationSentAt)}</span>
                <button className="text-xs text-primary hover:underline" onClick={() => agentApi.resendAlias(a.id).then(() => setNotice('Resent.')).catch((e) => setError(apiErrorMessage(e)))}>Resend</button>
              </>
            )}
            <button className="text-xs text-red-600 dark:text-red-400 hover:underline" onClick={() => agentApi.deleteAlias(a.id).then(load)}>Remove</button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 mt-3">
        <input className={input} type="email" placeholder="you@otheraddress.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button className={btn.primary} onClick={add} disabled={busy || !email.includes('@')}>Add</button>
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</p>}
      {notice && !error && <p className="text-xs text-green-700 dark:text-green-400 mt-2">{notice}</p>}
    </div>
  );
};

export default EmailAliasManager;
